import { UserPlusIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';

import {
  fetchWorkspaceUsers,
  setCurrentPage,
  setEmailSearch,
} from '../../store/workspaceUsersSlice';
import type { WorkspaceUserDto } from '../../types/workspace.types';

import AddUserModal from './AddUserModal';
import EditRoleModal from './EditRoleModal';
import RemoveUserDialog from './RemoveUserDialog';
import WorkspaceUserList from './WorkspaceUserList';
import WorkspaceUsersEmptyState from './WorkspaceUsersEmptyState';
import WorkspaceUsersErrorState from './WorkspaceUsersErrorState';

import { useDebounce } from '@hooks/useDebounce';
import { useAppDispatch, useAppSelector } from '@store/hooks';

const PAGE_SIZE = 20;

const ADD_USER_ROLES = new Set([
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'COMPANY_ADMIN',
]);

const MANAGE_USER_ROLES = new Set([
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'COMPANY_ADMIN',
]);

const WorkspaceUsersView: React.FC = () => {
  const dispatch = useAppDispatch();
  const { users, pagination, emailSearch, currentPage, status } =
    useAppSelector((s) => s.settingsWorkspaceUsers);

  const workspaceRoles: string[] = useAppSelector(
    (s: any) => s.auth?.user?.workspaceContext?.roles ?? []
  );

  const currentUserId: string | undefined = useAppSelector(
    (s: any) => s.auth?.user?.profile?.sub
  );

  const canAddUsers = workspaceRoles.some((r) =>
    ADD_USER_ROLES.has(r.toUpperCase())
  );

  const canManageUsers = workspaceRoles.some((r) =>
    MANAGE_USER_ROLES.has(r.toUpperCase())
  );

  const [rawSearch, setRawSearch] = useState('');
  const debouncedSearch = useDebounce(rawSearch, 300);

  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [editRoleTarget, setEditRoleTarget] = useState<{
    userId: string;
    currentRole: string;
  } | null>(null);

  const [removeTarget, setRemoveTarget] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);

  // Only dispatch search update when 0 chars (clear) or >= 3 chars
  useEffect(() => {
    if (debouncedSearch.length === 0 || debouncedSearch.length >= 3) {
      dispatch(setEmailSearch(debouncedSearch));
    }
  }, [debouncedSearch, dispatch]);

  // Fetch whenever page or emailSearch changes
  useEffect(() => {
    dispatch(
      fetchWorkspaceUsers({
        page: currentPage,
        pageSize: PAGE_SIZE,
        email: emailSearch || undefined,
      })
    );
  }, [currentPage, emailSearch, dispatch]);

  const handleRetry = useCallback(() => {
    dispatch(
      fetchWorkspaceUsers({
        page: currentPage,
        pageSize: PAGE_SIZE,
        email: emailSearch || undefined,
      })
    );
  }, [currentPage, emailSearch, dispatch]);

  const handleAddSuccess = useCallback(
    (message: string) => {
      setSuccessMessage(message);
      dispatch(
        fetchWorkspaceUsers({
          page: 1,
          pageSize: PAGE_SIZE,
          email: emailSearch || undefined,
        })
      );
      dispatch(setCurrentPage(1));
      setTimeout(() => setSuccessMessage(null), 5000);
    },
    [dispatch, emailSearch]
  );

  const handleActionSuccess = useCallback(() => {
    dispatch(
      fetchWorkspaceUsers({
        page: currentPage,
        pageSize: PAGE_SIZE,
        email: emailSearch || undefined,
      })
    );
  }, [dispatch, currentPage, emailSearch]);

  const handleEditRole = useCallback((user: WorkspaceUserDto) => {
    setEditRoleTarget({
      userId: user.userId,
      currentRole: user.applicationRole,
    });
  }, []);

  const handleRemoveUser = useCallback((user: WorkspaceUserDto) => {
    setRemoveTarget({ userId: user.userId, displayName: user.fullName });
  }, []);

  const isLoading = status === 'loading';

  return (
    <div className="w-full max-w-full overflow-hidden flex flex-col space-y-6 p-6">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-gray-800 tracking-tight text-center md:text-left">
          Workspace Users
        </h2>
        {canAddUsers && (
          <button
            type="button"
            onClick={() => setAddModalOpen(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors shadow-sm"
          >
            <UserPlusIcon className="w-4 h-4" />
            Add User
          </button>
        )}
      </div>

      {/* Success banner */}
      {successMessage && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          {successMessage}
        </div>
      )}

      {/* Search — rounded layout matching theme card */}
      <div>
        <input
          type="text"
          placeholder="Search by email..."
          value={rawSearch}
          onChange={(e) => setRawSearch(e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-200 rounded-md text-sm text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm"
        />
      </div>

      {/* Content area */}
      {status === 'failed' && (
        <WorkspaceUsersErrorState onRetry={handleRetry} />
      )}

      {status !== 'failed' && (
        <div className="bg-white rounded-md border border-gray-100 shadow-sm overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <span className="text-sm text-gray-500">Loading users...</span>
            </div>
          )}

          {!isLoading && users.length === 0 && (
            <WorkspaceUsersEmptyState isFiltered={rawSearch.length >= 3} />
          )}

          {!isLoading && users.length > 0 && (
            <div className="overflow-x-auto">
              <WorkspaceUserList
                users={users}
                currentUserId={currentUserId}
                onEditRole={canManageUsers ? handleEditRole : undefined}
                onRemoveUser={canManageUsers ? handleRemoveUser : undefined}
              />
            </div>
          )}
        </div>
      )}

      {pagination && (
        <div className="flex items-center justify-between pt-2 w-full">
          <span className="text-sm text-gray-500 font-medium">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => dispatch(setCurrentPage(currentPage - 1))}
              disabled={!pagination.hasPreviousPage}
              className="px-4 py-2 text-sm border border-gray-200 rounded-md text-gray-600 bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors shadow-sm"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => dispatch(setCurrentPage(currentPage + 1))}
              disabled={!pagination.hasNextPage}
              className="px-4 py-2 text-sm border border-gray-200 rounded-md text-gray-600 bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors shadow-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add User modal */}
      {canAddUsers && (
        <AddUserModal
          isOpen={isAddModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={handleAddSuccess}
        />
      )}

      {/* Edit Role modal */}
      {editRoleTarget && (
        <EditRoleModal
          userId={editRoleTarget.userId}
          currentRole={editRoleTarget.currentRole}
          isOpen={editRoleTarget !== null}
          onClose={() => setEditRoleTarget(null)}
          onSuccess={handleActionSuccess}
        />
      )}

      {/* Remove User dialog */}
      {removeTarget && (
        <RemoveUserDialog
          userId={removeTarget.userId}
          displayName={removeTarget.displayName}
          isOpen={removeTarget !== null}
          onClose={() => setRemoveTarget(null)}
          onSuccess={handleActionSuccess}
        />
      )}
    </div>
  );
};

export default WorkspaceUsersView;
